import { distinctUntilChanged } from "rxjs/operators";
import { combineLatest } from "rxjs";


/**
 * @param {SheetListController$Scope} $scope
 * @param {import('core-services/user.service').UserService} UserService
 * @param {import('core-services/provider-accounts.service').ProviderAccountsService} ProviderAccountsService
 * @param {import('core-services/sheets.service').SheetsService} SheetsService
 * @param {moment} moment
 */
export function SheetListController(
  $scope,
  UserService,
  ProviderAccountsService,
  SheetsService,
  Preference,
  moment,
) {

  $scope.loading = 0;
  $scope.sheets = [];
  $scope.providerAccounts = [];
  $scope.pendingSheets = [];
  $scope.preferences = {};
  $scope.pendingUpdateCount = 0;
  $scope.viewCapabilities = null;
  $scope.isDisabled = UserService.inAnyState(['setup']);

  if ($scope.setControllerName)
    $scope.setControllerName('SheetListCtrl');

  $scope.$watch('appData.user', function() {
    $scope.viewCapabilities =
      new SheetListViewCapabilities(new UserCapabilities($scope.appData.user));
  });

  $scope.isAddGoogleSheetContainerCollapsed = true;

  $scope.doCreateSheet = function(args) {
    $scope.isAddGoogleSheetContainerCollapsed = true;

    return SheetsService.createSheet({
      template: args.templateId,
      spreadsheetTitle: args.title,
      accounts: args.accountIds,
    });
  }
  $scope.doToggleCollapseForAddGoogleSheetContainer = function() {
    // -- @todo :: max-sheets check ($ctrl.sheets && $ctrl.sheets.length < $ctrl.maxSheets)
    $scope.$applyAsync((scope) => {
      $scope.isAddGoogleSheetContainerCollapsed = !$scope.isAddGoogleSheetContainerCollapsed;
    });
  }


  /**
   * Returns whether a pending sheet can be
   * retried (usually due to error or timeout).
   *
   */
  $scope.pendingSheetIsRetryable = function(sheet) {
    var fiveMinutesAgo = moment().subtract( moment.duration(5, 'minutes') );

    return sheet.state == "error" ||
             moment(sheet.lastUpdatedAt).isBefore(fiveMinutesAgo);
  }

  /**
   * Returns whether a sheet initialization can be canceled.
   *
   */
  $scope.pendingSheetIsCancelable = function(sheet) {
    return $scope.pendingSheetIsRetryable(sheet);
  };

  $scope.retryCreateNewSheet = function(sheet) {
    return SheetsService.retryPendingSheet(sheet);
  };

  $scope.cancelSheetCreation = function(sheet) {
    return SheetsService.cancelPendingSheet(sheet);
  };

  var getPreferences = function() {
    var querySuccess = function(preference, responseHeaders) {
      $scope.preferences[preference.name] = preference.selected;
      $scope.loading--;
    }
    function queryError(httpResponse) {
      $scope.loading--;
    }

    $scope.loading += 2;
    Preference.get({name: 'muteInitialUpdateWarning'}, querySuccess, queryError);
    Preference.get({name: 'muteAccountUnselectWarning'}, querySuccess, queryError);
  }

  getPreferences();

  SheetsService.sheets$
    .pipe(distinctUntilChanged((a, b) => {
      return angular.equals(a, b);
    }))
    .subscribe(sheets => {
      $scope.$applyAsync((scope) => {
        // @ts-ignore
        angular.copy(sheets, scope.sheets);
      })
    });

  SheetsService.pendingSheets$
    .pipe(distinctUntilChanged((a, b) => angular.equals(a, b)))
    .subscribe(pendingSheets => {
      $scope.$applyAsync((scope) => {
        // @ts-ignore
        angular.copy(pendingSheets, scope.pendingSheets)
      })
    });

  combineLatest(
    ProviderAccountsService.providerAccounts$,
    ProviderAccountsService.providerAccountsWithArchives$,
  ).subscribe(([providerAccounts, providerAccountsWithArchives]) => {
    $scope.$applyAsync((scope) => {
      const allAccounts = [
        ...providerAccounts,
        ...providerAccountsWithArchives,
      ];

      $scope.providerAccounts = allAccounts;
    });
  });

  UserService.user$.subscribe(user => {
    $scope.isDisabled = user?.state == 'setup';
  });
};


function UserCapabilities(user) {
  this.isLegacyFeedbotOverrideEnabled = function() {
    return user?.isNewFeedbotSheetsEnabled ?? false;
  }

  this.hasGoogleIdentity = function() {
    if (!user) return false;

    return user
      .identity
      .linkedIdentities
      .findIndex(function(ident) {
        return ident.provider == 'google-oauth2';
      }) >= 0;
  }


};

function SheetListViewCapabilities(userCapabilities) {
  /**
   * Returns whether or not the new Feeds-based flow is visible.
   */
  this.isNewSheetForFeedsVisible = true;

  if (!userCapabilities.hasGoogleIdentity()) {
    this.isNewGoogleSheetForFeedbotVisible = false;
  } else {
    this.isNewGoogleSheetForFeedbotVisible =
      !this.isNewSheetForFeedsVisible
        || userCapabilities.isLegacyFeedbotOverrideEnabled();
  }
}

export default [
  '$scope',
  'UserService',
  'ProviderAccountsService',
  'SheetsService',
  'PreferenceResource',
  'moment',
  SheetListController
];




// import { IScope as CollapsibleScope } from '../types/collapsible.controller';
// import type { SheetsService } from './sheets.service';
// import { V1 } from 'types/api';
// import { TillerAppController } from '../tiller-app.controller';
// import { UserService } from '../user/user.service';

/**
 * @typedef {import("types/api").V1.SheetFromAPI} SheetListController.SheetFromAPI
 * @typedef {import("types/api").V1.PendingSheetFromAPI} SheetListController.PendingSheetFromAPI
 * @typedef {import("types/api").ProviderAccountFromAPI} SheetListController.ProviderAccountFromAPI
 */

/**
 * @typedef {object} SheetListController.MyScope
 * @property {number} loading
 * @property {SheetListController.SheetFromAPI[] | null} sheets
 * @property {SheetListController.PendingSheetFromAPI[] | null} pendingSheets
 * @property {SheetListController.ProviderAccountFromAPI[]} providerAccounts
 * @property {any} preferences
 * @property {number} pendingUpdateCount
 * @property {any} viewCapabilities
 * @property {boolean} isDisabled
 * @property {boolean} isAddGoogleSheetContainerCollapsed
 * @property {() => void} doToggleCollapseForAddGoogleSheetContainer
 * @property {() => void} doCreateSheet
 * @property {(sheet: SheetListController.PendingSheetFromAPI) => boolean} pendingSheetIsRetryable
 * @property {(sheet: SheetListController.PendingSheetFromAPI) => boolean} pendingSheetIsCancelable
 * @property {() => void} refreshSheetList
 * @property {(sheet: { id: string }) => void} retryCreateNewSheet
 * @property {(sheet: { id: string }) => void} cancelSheetCreation
 * @property {import('../tiller-app.controller').TillerAppController$Scope['appData']} appData
 */

/**
 * @typedef {ng.IScope & import('../collapsible.controller').IScope & SheetListController.MyScope} SheetListController$Scope
 */
